import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PanoramaViewer from './PanoramaViewer';

interface Classroom {
  id: string;
  name: string;
  ageGroup: string;
  imageUrl: string;
  description: string;
  features: string[];
}

const ClassroomPanoramaTabs: React.FC = () => {
  const [activeClassroom, setActiveClassroom] = useState<string>('starfish-one');

  const classrooms: Classroom[] = [
    {
      id: 'starfish-one',
      name: 'Starfish Room One',
      ageGroup: '6 - 18 months',
      imageUrl: '/images/newPanos/Starfish#1.jpg',
      description: 'Our first starfish classroom designed for exploration and early learning through play-based activities.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'starfish-two',
      name: 'Starfish Room Two',
      ageGroup: '15 - 24 months',
      imageUrl: '/images/Starfish2_Pano.jpg',
      description: 'Our second starfish classroom with similar design philosophy for toddler development.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'seal-one',
      name: 'Seal Room One',
      ageGroup: '2 - 2.5 Years',
      imageUrl: '/images/Sealroom1_Pano.jpg',
      description: 'Pre-K classroom focused on developing school readiness and social skills.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'seal-two',
      name: 'Seal Room Two',
      ageGroup: '2.5 - 3.5 Years',
      imageUrl: '/images/Sealroom2_Pano.jpg',
      description: 'Our second seal classroom with engaging pre-K learning environments.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'orca',
      name: 'Dolphin Room',
      ageGroup: '3 - 5 Years',
      imageUrl: '/images/Orca_Pano.jpg',
      description: 'Advanced pre-K room preparing children for kindergarten with structured learning.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'dolphin',
      name: 'Orca Room',
      ageGroup: '4 - School Age',
      imageUrl: '/images/Dolphin_Pano.jpg',
      description: 'Our kindergarten prep classroom with school-ready learning environments.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    }
  ];

  const activeClassroomData = classrooms.find(c => c.id === activeClassroom) || classrooms[0];

  const tabVariants = {
    inactive: { 
      backgroundColor: '#e5e7eb',
      color: '#6b7280',
      scale: 0.95
    },
    active: { 
      backgroundColor: '#1e3a8a',
      color: '#ffffff',
      scale: 1
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6">
          {classrooms.map((classroom) => (
            <motion.button
              key={classroom.id}
              onClick={() => setActiveClassroom(classroom.id)}
              className={`px-3 py-2 md:px-4 md:py-3 rounded-lg font-semibold text-xs md:text-sm transition-all duration-300 shadow-lg min-w-[100px] md:min-w-[120px] ${
                activeClassroom === classroom.id
                  ? 'bg-dblue text-white shadow-lg'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              variants={tabVariants}
              initial="inactive"
              animate={activeClassroom === classroom.id ? "active" : "inactive"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="text-center">
                <div className="font-bold leading-tight">
                  {classroom.name.replace(' Room', '').replace(' One', ' 1').replace(' Two', ' 2')}
                </div>
                <div className="text-xs opacity-80 mt-1">{classroom.ageGroup}</div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Active Classroom Info */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeClassroom}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center mb-6"
          >
            <h2 className="text-3xl font-bold text-dblue mb-2">
              {activeClassroomData.name}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-4">
              {activeClassroomData.description}
            </p>
            {/*}
            <div className="flex flex-wrap justify-center gap-2">
              {activeClassroomData.features.map((feature, index) => (
                <span
                  key={index}
                  className="bg-yellow-100 text-yellow-800 text-xs px-3 py-1 rounded-full"
                >
                  {feature}
                </span>
              ))}
            </div>
            */}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Panorama Viewer */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeClassroom}
          variants={contentVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="max-w-5xl mx-auto"
        >
          <PanoramaViewer
            imageUrl={activeClassroomData.imageUrl}
            title={activeClassroomData.name}
            description={`Take a 360° tour of our ${activeClassroomData.name.toLowerCase()} designed for children aged ${activeClassroomData.ageGroup.toLowerCase()}.`}
            autoRotate={-2}
            className="w-full"
          />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Hints */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-8 text-center"
      >
        <div className="bg-blue-50 rounded-lg p-6 max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-3">
            <svg className="w-5 h-5 text-dblue mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-dblue">Navigation Tips</span>
          </div>
          <p className="text-sm text-gray-600">
            Click the classroom tabs above to explore different learning spaces. 
            Each panorama shows the unique features and layout of that classroom.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ClassroomPanoramaTabs;
